
import React, { useState, useEffect } from 'react';
import { CalendarCheck } from 'lucide-react';
import {
    getClosingStatus,
    calculateClosing,
    approveClosing
} from '../../../usecases/accounting-usecase';
import AccountSearchDropdown from '../../molecules/AccountSearchDropdown/view';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import Modal from '../../atoms/modal/view';
import ButtonBasic from '../../atoms/buttonBasic/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import { Form } from 'react-bootstrap';
import './style.css';
import ModalBtn from '../../molecules/modalButton/view';
import SubaccountSearchDropdown from '../../molecules/SubaccountSearchDropdown/view';

import { FiCalendar, FiCheckCircle, FiAlertTriangle, FiBookOpen, FiInfo } from 'react-icons/fi';
import Tooltip from '@mui/material/Tooltip';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';

const AccountingClosureModal = ({ id = "accounting-closure" }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [utilityAccount, setUtilityAccount] = useState(null);
    const [lossAccount, setLossAccount] = useState(null);
    const [statusData, setStatusData] = useState(null);

    useEffect(() => {
        fetchStatus();
    }, [year]);

    const fetchStatus = async () => {
        try {
            const data = await getClosingStatus(year);
            setStatusData(data);
            if (data) {
                if (data.utility_account_id) setUtilityAccount({ id: data.utility_account_id });
                if (data.loss_account_id) setLossAccount({ id: data.loss_account_id });
            } else {
                setUtilityAccount(null);
                setLossAccount(null);
                setStatusData(null);
            }
        } catch (error) {
            console.error("Error fetching closing status", error);
        }
    };

    const handleCalculate = async () => {
        if (!utilityAccount?.id || !lossAccount?.id) {
            dispatchCustomEvent('popup', { type: 'error', message: "Debe seleccionar las cuentas de utilidad y pérdida." });
            dispatchCustomEvent('loading-btn-calculate', {});
            return;
        }
        try {
            const result = await calculateClosing({
                year: parseInt(year),
                utility_account_id: parseInt(utilityAccount.id),
                loss_account_id: parseInt(lossAccount.id)
            });
            setStatusData(result);
            dispatchCustomEvent('popup', { type: 'success', message: 'Cierre calculado correctamente (Borrador)' });
        } catch (error) {
            dispatchCustomEvent('popup', { type: 'error', message: 'Error al calcular cierre: ' + (error.message || 'Error desconocido') });
        } finally {
            dispatchCustomEvent('loading-btn-calculate', {});
        }
    };

    const handleApprove = () => {
        dispatchCustomEvent('notification-confirm-approve-closure', { data: { year } });
    };

    const executeApproval = async () => {
        try {
            const result = await approveClosing({ year: parseInt(year) });
            setStatusData(result);
            dispatchCustomEvent('popup', { type: 'success', message: 'Cierre APROBADO exitosamente' });
            dispatchCustomEvent('reload-comprobantes', {});
        } catch (error) {
            dispatchCustomEvent('popup', { type: 'error', message: 'Error al aprobar: ' + error.message });
        } finally {
            dispatchCustomEvent('loading-btn-approve', {});
        }
    };

    return (
        <ModalBtn
            id='accounting-closure'
            title="Cierre Contable Anual"
            icon={<CalendarCheck size={20} />}
            style={{ width: '65vw', maxWidth: '95vw' }}
        >
            <div className="new-closure-container">
                {/* Left Column (30%) */}
                <div className="new-closure-sidebar">
                    <h5 className="new-closure-section-title">Cierre Contable Anual</h5>
                    <p className="new-closure-section-desc">
                        Permite realizar el cierre contable anual cancelando las cuentas de resultado (ingresos, gastos, costos) y trasladando el resultado neto (utilidad o pérdida) a las cuentas de patrimonio correspondientes.
                    </p>
                </div>

                {/* Right Column (70%) */}
                <div className="new-closure-form-right">
                    <div className="new-closure-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <label className="closure-label">Año Fiscal</label>
                            <Form.Select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                {[...Array(5)].map((_, i) => {
                                    const y = new Date().getFullYear() - i + 1;
                                    return <option key={y} value={y}>{y}</option>
                                })}
                            </Form.Select>
                        </Form.Group>
                    </div>

                    <hr className="new-closure-separator" />

                    <div className="new-closure-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                        <Form.Group style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="closure-label mb-0">Cuenta Utilidad del Ejercicio</label>
                                <Tooltip title="Selecciona la cuenta de patrimonio donde se registrará la utilidad del ejercicio (ej. Clase 3)." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <SubaccountSearchDropdown
                                onSelected={(e) => setUtilityAccount(e)}
                                selected={utilityAccount}
                            />
                        </Form.Group>
                        <Form.Group style={{ flex: 1 }}>
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <label className="closure-label mb-0">Cuenta Pérdida del Ejercicio</label>
                                <Tooltip title="Selecciona la cuenta de patrimonio donde se registrará la pérdida del ejercicio (ej. Clase 3)." arrow placement="top">
                                    <span>
                                        <FiInfo className="text-muted cursor-pointer" size={14} />
                                    </span>
                                </Tooltip>
                            </div>
                            <SubaccountSearchDropdown
                                onSelected={(e) => setLossAccount(e)}
                                selected={lossAccount}
                            />
                        </Form.Group>
                    </div>

                    {statusData && (
                        <>
                            <hr className="new-closure-separator" />
                            <div className={`status-alert ${statusData.status === 'APPROVED' ? 'approved' : 'pending'}`}>
                                {statusData.status === 'APPROVED' ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <FiCheckCircle size={18} />
                                        <strong>Cierre Aprobado</strong>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center gap-2">
                                        <FiAlertTriangle size={18} />
                                        <div>
                                            <strong>Borrador Pendiente</strong> - Revise el comprobante generado antes de aprobar.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <div className="closure-footer d-flex justify-content-end gap-2 mt-3">
                        <ButtonBasic
                            title="Cerrar"
                            type="option"
                            onClick={() => dispatchCustomEvent('close-modal', {})}
                        />

                        {(!statusData || statusData.status === 'PENDING') && (
                            <ButtonPrimary
                                id="calculate"
                                title={statusData ? "Recalcular" : "Generar Cierre"}
                                onClick={handleCalculate}
                            />
                        )}

                        {statusData?.status === 'PENDING' && (
                            <ButtonPrimary
                                id="approve"
                                title="Aprobar Cierre"
                                onClick={handleApprove}
                                style={{ backgroundColor: 'var(--color-success)', color: 'white', border: 'none' }}
                            />
                        )}
                    </div>
                </div>
            </div>
            <ConfirmPopUp
                eventKey="approve-closure"
                msg="¿Está seguro de que desea aprobar el cierre contable para este año? Esta acción es irreversible y generará los asientos permanentes."
                onAcept={executeApproval}
            />
        </ModalBtn>
    );
};

export default AccountingClosureModal;
